import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismIcon,
  PrismNumberInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import type { RoomOffsetHierarchy } from 'lumen/models/recommendation-strategy';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { resolveOffsetValue } from 'lumen/utils/pricing-assistant/room-hierarchy-settings';
import { localCopy } from 'tracked-toolbox';

import styles from './room-item.module.css';

function countRoomsInSubtree(room: RoomOffsetHierarchy): number {
  return (
    1 +
    Object.values(room.children).reduce(
      (count, child) => count + countRoomsInSubtree(child),
      0,
    )
  );
}

export type RoomSelectOption = {
  badge?: string;
  badgeType?: 'neutral';
  label: string;
};

interface SettingsRecommendationsRoomHierarchyRoomItemSignature {
  Args: {
    activeRoomCode: string | null;
    editingRoomCode: string | null;
    invalidRoomCodes?: string[];
    isEditMode: boolean;
    onActivate: (roomCode: string | null) => void;
    onAddChildRoom: (parentRoomCode: string) => void;
    onCommitRoom: (
      oldRoomCode: string,
      newRoomCode: string,
      minOffsetValue: number | null,
      maxOffsetValue: number | null,
    ) => void;
    onOffsetRangeErrorChange: (hasError: boolean) => void;
    onRemoveRoom: (roomCode: string) => void;
    onRoomEdit: (roomCode: string | null) => void;
    parentRoomCode?: string;
    pathRoomCode?: string;
    room: { roomCode: string; roomHierarchy: RoomOffsetHierarchy };
    roomSelectOptions: RoomSelectOption[];
  };
}

export default class RoomItem extends Component<SettingsRecommendationsRoomHierarchyRoomItemSignature> {
  @service declare alerting: Services['alerting'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  @tracked hasOffsetRangeError = false;

  // holds the room code the user has selected in the dropdown but not yet confirmed
  @tracked localPendingRoomCode: string | null = null;
  /**
   *  Re-runs whenever localPendingRoomCode changes, so the input
   * is focused the moment a room is selected from the dropdown.
   */
  focusOffsetInput = modifier((element: HTMLElement): void => {
    if (!this.localPendingRoomCode) {
      return;
    }

    const input = element.querySelector('input');

    input?.focus();
  });
  /**
   * Plain (untracked) mirror of the last value reported to the parent.
   * setHasOffsetRangeError can run from inside resetRoomEditMode's tracked
   * computation, so it can't read hasOffsetRangeError itself to dedupe.
   * Reading then writing the same tracked cell in one computation is an
   * autotracking violation. This field carries no tag, so it's safe to
   * read and write from anywhere.
   */
  lastReportedOffsetRangeError = false;

  /**
   * Re-seeds from the hierarchy whenever the underlying node's offset changes
   * (e.g. a commit, or Cancel's rollbackAttributes) while still allowing the
   * user to type freely in between.
   */
  @localCopy('seedMaxOffsetValue') declare maxOffsetValue: number | null;
  @localCopy('seedMinOffsetValue') declare minOffsetValue: number | null;

  resetRoomEditMode = modifier(
    (
      _element: Element,
      [isEditMode, isRoomEditMode, roomCode]: [boolean, boolean, string],
    ): void => {
      if (!isEditMode || (!isRoomEditMode && roomCode)) {
        this.localPendingRoomCode = null;
        // Leaving edit mode without a successful commit (Save, Cancel, or the
        // pencil switching to a different room) must not leave a stale error
        // behind for the next edit. The invalid draft is discarded below, so
        // there is nothing left to gate Save against; committed-but-invalid
        // legacy data is gated at the model level via isStrategyInvalid.
        this.setHasOffsetRangeError(false);
        // Reset the offset drafts too. An invalid attempt never gets
        // committed, so the underlying hierarchy stays the same, and
        // @localCopy only re-derives when its seed changes, not when edit
        // mode exits.
        this.minOffsetValue = this.seedMinOffsetValue;
        this.maxOffsetValue = this.seedMaxOffsetValue;
      }
    },
  );

  get childCount(): number {
    return countRoomsInSubtree(this.args.room.roomHierarchy) - 1;
  }

  get currencySymbol(): string {
    return this.hotelAndMarketService.hotel.currencySymbol;
  }

  get currentRoomCode(): string {
    return this.args.room.roomCode;
  }

  get displayMaxOffsetValue(): number {
    return resolveOffsetValue(this.maxOffsetValue);
  }

  // Non-edit display uses 0 for an empty field, consistent with what save persists.
  get displayOffsetValue(): number {
    return resolveOffsetValue(this.minOffsetValue);
  }

  get hasRoomChildren(): boolean {
    return Boolean(Object.keys(this.args.room.roomHierarchy.children).length);
  }

  get isActive(): boolean {
    return this.args.activeRoomCode === this.currentRoomCode;
  }

  get isBaseRoom(): boolean {
    return (
      this.currentRoomCode ===
      this.hotelAndMarketService.hotel.pricingIntegration
        .pms_leading_room_pms_id
    );
  }

  /** True when this room is structurally flagged (COA-4065), independent of any live edit. */
  get isFlaggedInvalid(): boolean {
    return (this.args.invalidRoomCodes ?? []).includes(this.currentRoomCode);
  }

  /** True when this room is an ancestor of the active room in the navigation path (light-blue highlight). */
  get isInPath(): boolean {
    return this.args.pathRoomCode === this.currentRoomCode;
  }

  /**
   * True while this room's own draft is invalid (ephemeral, from a live
   * edit) or the room is flagged in invalidRoomCodes (a structurally
   * invalid, possibly untouched room). Drives the visible error styling,
   * which must show even before the user has interacted with an
   * auto-opened room (COA-4065).
   */
  get isOffsetRangeErrorVisible(): boolean {
    return this.hasOffsetRangeError || this.isFlaggedInvalid;
  }

  get isOffsetRangeInvalid(): boolean {
    return (
      resolveOffsetValue(this.maxOffsetValue) <
      resolveOffsetValue(this.minOffsetValue)
    );
  }

  /*
   * editingRoomCode is a single value owned by the table parent -- only one
   * room can be manually pencil-clicked into edit mode at a time. A room
   * flagged in invalidRoomCodes auto-opens into the same edit-state UI
   * independently of that slot, so more than one room can be open at once
   * (COA-4065).
   */
  get isRoomEditMode(): boolean {
    return (
      this.args.editingRoomCode === this.currentRoomCode ||
      this.isFlaggedInvalid
    );
  }

  /**
   * Newly created room items start with no roomCode and no offset;
   * therefore the offset input is cleared with null so the placeholder "0"
   * shows instead of a real zero the user would have to delete before typing.
   */
  get seedMaxOffsetValue(): number | null {
    return this.currentRoomCode === ''
      ? null
      : (this.args.room.roomHierarchy.maximum_offset?.value ?? null);
  }

  get seedMinOffsetValue(): number | null {
    return this.currentRoomCode === ''
      ? null
      : this.args.room.roomHierarchy.offset.value;
  }

  get selectedRoomOption(): RoomSelectOption | undefined {
    const roomCode = this.localPendingRoomCode ?? this.currentRoomCode;
    return this.args.roomSelectOptions.find((room) => room.label === roomCode);
  }

  get showRoomSelect(): boolean {
    return (
      this.args.isEditMode && (!this.currentRoomCode || this.isRoomEditMode)
    );
  }

  willDestroy(): void {
    super.willDestroy();
    this.args.onOffsetRangeErrorChange(false);
  }

  private async confirmAndRemove(): Promise<void> {
    const count = countRoomsInSubtree(this.args.room.roomHierarchy);
    const confirmed = await this.alerting.showWarningAlert({
      dangerMode: true,
      title: this.intl.t(
        'pricing_assistant.recommendations.settings.room_hierarchy.remove_room_title',
      ),
      text: this.intl.t(
        'pricing_assistant.recommendations.settings.room_hierarchy.remove_room_description',
        { count: this.childCount },
      ),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t(
            'pricing_assistant.recommendations.settings.room_hierarchy.remove_n_rooms',
            { count },
          ),
        },
      },
    });

    if (confirmed) {
      this.args.onRemoveRoom(this.currentRoomCode);
    }
  }

  private setHasOffsetRangeError(value: boolean): void {
    this.hasOffsetRangeError = value;

    if (this.lastReportedOffsetRangeError === value) {
      // Leaving/re-entering edit mode re-runs resetRoomEditMode, which would
      // otherwise re-report a value the parent was already told about.
      return;
    }

    this.lastReportedOffsetRangeError = value;
    this.args.onOffsetRangeErrorChange(value);
  }

  @action
  activate(): void {
    this.args.onActivate(this.isActive ? null : this.currentRoomCode);
  }

  @action
  handleClickOutside(): void {
    if (this.isOffsetRangeInvalid) {
      this.setHasOffsetRangeError(true);
      return;
    }

    this.setHasOffsetRangeError(false);

    const resolvedMinOffsetCurrent = resolveOffsetValue(this.minOffsetValue);

    const resolvedMinOffsetOriginal = resolveOffsetValue(
      this.args.room.roomHierarchy.offset.value,
    );

    const resolvedMaxOffsetCurrent = resolveOffsetValue(this.maxOffsetValue);

    const resolvedMaxOffsetOriginal = resolveOffsetValue(
      this.args.room.roomHierarchy.maximum_offset?.value ?? null,
    );

    const roomCodeChanged = this.localPendingRoomCode !== null;

    const minOffsetChanged =
      resolvedMinOffsetCurrent !== resolvedMinOffsetOriginal;

    const maxOffsetChanged =
      resolvedMaxOffsetCurrent !== resolvedMaxOffsetOriginal;

    if (roomCodeChanged || minOffsetChanged || maxOffsetChanged) {
      this.args.onCommitRoom(
        this.currentRoomCode,
        this.localPendingRoomCode ?? this.currentRoomCode,
        resolvedMinOffsetCurrent,
        resolvedMaxOffsetCurrent,
      );
    }

    this.localPendingRoomCode = null;
    this.args.onRoomEdit(null);
  }

  @action
  handleMaxOffsetChange(value: number | null): void {
    this.maxOffsetValue = value;
  }

  @action
  handleMinOffsetChange(value: number | null): void {
    this.minOffsetValue = value;
  }

  @action
  handleOffsetKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }

    if (!this.localPendingRoomCode && !this.currentRoomCode) {
      return;
    }

    if (this.isOffsetRangeInvalid) {
      this.setHasOffsetRangeError(true);
      return;
    }

    this.setHasOffsetRangeError(false);

    this.args.onCommitRoom(
      this.currentRoomCode,
      this.localPendingRoomCode ?? this.currentRoomCode,
      resolveOffsetValue(this.minOffsetValue),
      resolveOffsetValue(this.maxOffsetValue),
    );

    this.localPendingRoomCode = null;
  }

  @action
  handlePencilClick(): void {
    this.args.onRoomEdit(this.currentRoomCode);
  }

  @action
  handleRemoveClick(): void {
    if (this.hasRoomChildren) {
      void this.confirmAndRemove();
    } else {
      this.args.onRemoveRoom(this.currentRoomCode);
    }
  }

  @action
  handleRoomCodeChange(option: RoomSelectOption): void {
    if (this.args.parentRoomCode) {
      // Child rooms have an offset to fill, so defer the commit until Enter.
      this.localPendingRoomCode = option.label;
    } else {
      // Root rooms have no offset — commit the rename immediately with null offsets.
      this.args.onCommitRoom(this.currentRoomCode, option.label, null, null);
    }
  }

  @action
  onAddChildRoom(): void {
    this.args.onAddChildRoom(this.currentRoomCode);
  }

  <template>
    {{#if this.showRoomSelect}}
      <div
        class="{{styles.room-edit-wrapper}}
          {{if @room.roomCode styles.room-context}}"
        {{outsideClickHandler this.handleClickOutside}}
        {{this.resetRoomEditMode
          @isEditMode
          this.isRoomEditMode
          @room.roomCode
        }}
      >
        <PrismSelectInput
          @onChange={{this.handleRoomCodeChange}}
          @options={{@roomSelectOptions}}
          @placeholder={{t
            "pricing_assistant.recommendations.settings.room_hierarchy.select_room"
          }}
          @unsafe_initiallyOpened={{unless @room.roomCode true}}
          @unsafe_matchTriggerWidth={{true}}
          @value={{this.selectedRoomOption}}
          data-test-id="dropdown-room-options"
        />
        {{#if @parentRoomCode}}
          <div class={{styles.offset-edit}} {{this.focusOffsetInput}}>
            <span class="text-200--regular {{styles.parent-label}}">=
              {{@parentRoomCode}}
              +</span>
            <PrismNumberInput
              @hasErrors={{this.isOffsetRangeErrorVisible}}
              @inlineLabel={{this.currencySymbol}}
              @isSmall={{true}}
              @isTouched={{true}}
              @min={{0}}
              @onValueChange={{this.handleMinOffsetChange}}
              @placeholder="0"
              @removePadding={{true}}
              @value={{this.minOffsetValue}}
              data-test-id="input-offset-min"
              {{on "keydown" this.handleOffsetKeydown}}
            />
            <span class="text-200--regular {{styles.parent-label}}">{{t
                "pricing_assistant.recommendations.settings.room_hierarchy.offset_to_separator"
              }}</span>
            <PrismNumberInput
              @hasErrors={{this.isOffsetRangeErrorVisible}}
              @inlineLabel={{this.currencySymbol}}
              @isSmall={{true}}
              @isTouched={{true}}
              @min={{0}}
              @onValueChange={{this.handleMaxOffsetChange}}
              @placeholder="0"
              @removePadding={{true}}
              @value={{this.maxOffsetValue}}
              data-test-id="input-offset-max"
              {{on "keydown" this.handleOffsetKeydown}}
            />
          </div>
          {{#if this.isOffsetRangeErrorVisible}}
            <div
              class="text-200--medium text-critical-default flex flex-row gap-100"
              data-test-id="offset-range-error"
            >
              <PrismIcon
                @color="critical"
                @name="circle-error-filled"
                @size="small"
              />
              {{t
                "pricing_assistant.recommendations.settings.room_hierarchy.max_offset_below_min"
              }}
            </div>
          {{/if}}
        {{/if}}
      </div>

    {{else}}
      <div
        class="{{styles.room-view-wrapper}}
          {{if @isEditMode styles.room-view-wrapper-with-actions}}
          {{if
            this.isActive
            styles.room-view-wrapper-active
            (if this.isInPath styles.room-context)
          }}"
        data-test-id="container-room-item"
        {{this.resetRoomEditMode
          @isEditMode
          this.isRoomEditMode
          @room.roomCode
        }}
      >
        <button
          class={{styles.room-info}}
          type="button"
          {{on "click" this.activate}}
        >
          <span class={{styles.room-code-row}}>
            <span class={{styles.room-code}}>{{@room.roomCode}}</span>
            {{#if this.isBaseRoom}}
              <PrismBadge
                @isSmall={{true}}
                @label={{t
                  "pricing_assistant.settings.panel.section.rooms.leading_room"
                }}
                @type="neutral"
                class={{styles.base-room-badge}}
              />
            {{/if}}
          </span>
          {{#if @parentRoomCode}}
            <span
              class="text-200--regular {{styles.offset-read}}"
              data-test-id="display-offset"
            >
              <span>=</span>
              <span>{{@parentRoomCode}}</span>
              <span>+</span>
              <span>{{this.currencySymbol}}{{this.displayOffsetValue}}</span>
              <span>{{t
                  "pricing_assistant.recommendations.settings.room_hierarchy.offset_to_separator"
                }}</span>
              <span>{{this.currencySymbol}}{{this.displayMaxOffsetValue}}</span>
            </span>
          {{/if}}
        </button>
        {{#if this.hasRoomChildren}}
          <span class={{styles.child-indicator}}>
            <PrismBadge
              @isSmall={{true}}
              @label={{this.childCount}}
              @type="neutral"
              data-test-id="badge-child-count"
            />
            <PrismIcon @name="chevron-right" @size="small" />
          </span>
        {{/if}}
        {{#if @isEditMode}}
          <div class={{styles.actions}}>
            <PrismButton
              @buttonType="ghost"
              @icon="edit-pencil"
              @onClick={{this.handlePencilClick}}
              @size="small"
              data-test-id="button-edit-pencil"
            />
            {{#if @parentRoomCode}}
              <PrismButton
                @buttonType="ghost"
                @icon="bin"
                @onClick={{this.handleRemoveClick}}
                @size="small"
                data-test-id="button-remove-room"
              />
            {{/if}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="plus"
              @label={{t
                "pricing_assistant.recommendations.settings.room_hierarchy.add_child_room"
              }}
              @onClick={{this.onAddChildRoom}}
              @size="small"
              data-test-id="button-add-child"
            />
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
